/**
 * Schémas Zod du générateur paramétrique d'exercices ("Blueprint").
 *
 * `spec` est un document JSONB validé app-side (pas de graphe SQL normalisé —
 * à l'image de `blog_articles.blocks`). Le Solve Path fusionne narration et
 * calcul : chaque étape peut porter une `computation` optionnelle, ce qui
 * garde le déroulé pédagogique du corrigé source comme SEULE source de vérité
 * (pas de liste de "functions" séparée à maintenir en synchro).
 *
 * Indépendant du système DAG solve-backward (aucune référence à
 * objets_math/gestes/types_de_probleme/misconceptions) — décision de cadrage
 * explicite : le Blueprint doit fonctionner en l'absence totale du DAG.
 */

import { z } from 'zod';
import { SchoolLevelSchema } from '../schemas/themes';

// ── Identifiants ─────────────────────────────────────────────────────────────
// Noms de paramètres/résultats : identifiants "variable-like", utilisés comme
// clés de portée (scope) pour evalExpr et comme placeholders `{nom}` dans les
// textes. Chaque nom (paramètre ou résultat produit par le solve path) doit
// être unique dans tout le Blueprint — vérifié par validateBlueprintSpec.

const NAME_REGEX = /^[a-z][a-zA-Z0-9_]*$/;
export const BlueprintNameSchema = z
  .string()
  .min(1)
  .max(40)
  .regex(
    NAME_REGEX,
    'Nom invalide : lettres/chiffres/underscore, doit commencer par une minuscule',
  );

// ── Paramètres ───────────────────────────────────────────────────────────────

export const BlueprintParameterTypeSchema = z.enum([
  'integer',
  'decimal',
  'positiveInteger',
  'positiveDecimal',
]);
export type BlueprintParameterType = z.infer<typeof BlueprintParameterTypeSchema>;

export const BlueprintParameterSchema = z
  .object({
    name: BlueprintNameSchema,
    type: BlueprintParameterTypeSchema,
    min: z.number(),
    max: z.number(),
    decimals: z.number().int().min(0).max(4).optional(),
  })
  .refine((p) => p.min <= p.max, { message: 'min doit être ≤ max', path: ['min'] })
  .refine((p) => !(p.type === 'positiveInteger' || p.type === 'positiveDecimal') || p.min > 0, {
    message: 'min doit être strictement positif pour un type positive*',
    path: ['min'],
  });
export type BlueprintParameter = z.infer<typeof BlueprintParameterSchema>;

// ── Contraintes (mini-langage déclaratif, §8 de la spec) ────────────────────

export const BlueprintConstraintSchema = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('compare'),
    left: z.string().min(1).max(200),
    operator: z.enum(['>', '>=', '<', '<=', '==', '!=']),
    right: z.string().min(1).max(200),
  }),
  z.object({
    type: z.literal('range'),
    variable: BlueprintNameSchema,
    min: z.number(),
    max: z.number(),
  }),
  z.object({
    type: z.literal('integer'),
    expression: z.string().min(1).max(200),
  }),
]);
export type BlueprintConstraint = z.infer<typeof BlueprintConstraintSchema>;

// ── Templates d'énoncé (2-3 formulations, §11) ───────────────────────────────
// Ne référencent que des noms de PARAMÈTRES (jamais un résultat calculé —
// l'énoncé ne doit pas fuiter la réponse). Vérifié par validateBlueprintSpec.

export const BlueprintTemplateSchema = z.object({
  id: z.string().min(1).max(40),
  text_md: z.string().min(1).max(4000),
});
export type BlueprintTemplate = z.infer<typeof BlueprintTemplateSchema>;

// ── Solve Path — chaîne LINÉAIRE narration + calcul (§12, décision de cadrage) ─
// Chaque étape porte le texte pédagogique tel que segmenté depuis le corrigé
// source, et optionnellement le calcul qu'elle introduit (le résultat devient
// disponible pour les étapes suivantes et les questions). Pas de branches.

export const BlueprintComputationSchema = z.object({
  functionId: z.string().min(1),
  resultName: BlueprintNameSchema,
  /** Expressions (evalExpr) référençant des paramètres ou résultats déjà connus, dans l'ordre du signature de la fonction. */
  args: z.array(z.string().min(1).max(200)).max(6),
});
export type BlueprintComputation = z.infer<typeof BlueprintComputationSchema>;

export const BlueprintSolveStepSchema = z.object({
  id: z.string().min(1).max(40),
  /** Style §12 : "Indices", "Action", "Outil", "Calcul", "Conclusion"… libre, reflète la pédagogie du corrigé source. */
  label: z.string().min(1).max(80),
  text_md: z.string().min(1).max(2000),
  computation: BlueprintComputationSchema.optional(),
});
export type BlueprintSolveStep = z.infer<typeof BlueprintSolveStepSchema>;

// ── Règles de distracteurs (QCM, déterministes, §9 — sans lien au catalogue DAG) ─

const misconceptionField = { misconceptionLabel: z.string().max(200).optional() };

export const BlueprintDistractorRuleSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('offset'), amount: z.number(), ...misconceptionField }),
  z.object({ kind: z.literal('multiply'), factor: z.number(), ...misconceptionField }),
  z.object({ kind: z.literal('swapArgs'), functionId: z.string().min(1), ...misconceptionField }),
  z.object({
    kind: z.literal('formulaRef'),
    functionId: z.string().min(1),
    ...misconceptionField,
  }),
]);
export type BlueprintDistractorRule = z.infer<typeof BlueprintDistractorRuleSchema>;

// ── Questions ────────────────────────────────────────────────────────────────

export const BlueprintAnswerModeSchema = z.enum(['qcm', 'calculator']);
export type BlueprintAnswerMode = z.infer<typeof BlueprintAnswerModeSchema>;

export const BlueprintQuestionSchema = z
  .object({
    id: z.string().min(1).max(40),
    prompt_md: z.string().min(1).max(2000),
    answerMode: BlueprintAnswerModeSchema,
    /** Le nom du résultat (paramètre ou solvePath.computation.resultName) attendu comme réponse. */
    resultName: BlueprintNameSchema,
    distractorRules: z.array(BlueprintDistractorRuleSchema).max(6).optional(),
    tolerance: z.number().positive().optional(),
    unitLabel: z.string().max(20).optional(),
    /**
     * Étapes du solve path qui constituent le corrigé DE CETTE question
     * (ids de `solvePath[].id`). Absent ⇒ tout le solve path, comportement
     * historique de `buildSolvePathExplanation` (simplification MVP 0221 :
     * chaque correction recopiait l'intégralité du corrigé).
     */
    solveStepIds: z.array(z.string().min(1).max(40)).max(20).optional(),
  })
  .refine((q) => q.answerMode !== 'qcm' || (q.distractorRules?.length ?? 0) > 0, {
    message: 'Une question QCM doit avoir au moins une règle de distracteur',
    path: ['distractorRules'],
  })
  .refine((q) => q.answerMode !== 'calculator' || q.tolerance !== undefined, {
    message: 'Une question en mode calculatrice doit déclarer une tolérance',
    path: ['tolerance'],
  });
export type BlueprintQuestion = z.infer<typeof BlueprintQuestionSchema>;

// ── Visuel paramétré (graphique de fonction / tableau, §14) ─────────────────
// Gabarit d'un bloc visuel V2 (function_graph | table) dont les champs
// numériques peuvent référencer des noms de paramètres déjà identifiés
// (JAMAIS un résultat du solve path — même règle que les templates d'énoncé :
// ne jamais fuiter la réponse). Deux mécanismes de substitution, à ne pas
// confondre :
//  - "expression templatée" ({nom} substitué via renderTemplate, tout le
//    reste de la chaîne — notamment la variable libre "x" — reste intact) :
//    utilisée pour l'expression d'une fonction, qui doit rester une fonction
//    de x après rendu (ex. "{a}*x^2+{b}*x+{c}").
//  - "nombre templaté" (littéral, ou expression evalExpr entièrement résolue
//    en un nombre, comme les args de computation/contraintes, ex. "2*rayon") :
//    utilisée pour des coordonnées/bornes qui n'ont pas de variable libre.

export const BlueprintTemplatedExpressionSchema = z.string().min(1).max(400);
export type BlueprintTemplatedExpression = z.infer<typeof BlueprintTemplatedExpressionSchema>;

export const BlueprintTemplatedNumberSchema = z.union([z.number(), z.string().min(1).max(200)]);
export type BlueprintTemplatedNumber = z.infer<typeof BlueprintTemplatedNumberSchema>;

export const BlueprintVisualFunctionSchema = z.object({
  id: z.string().min(1).max(64),
  /** Fonction de x, ex. "{a}*x^2+{b}*x+{c}" — rendu via renderTemplate, x reste libre. */
  expression: BlueprintTemplatedExpressionSchema,
  label: z.string().max(160).optional(),
  color: z.string().max(40).optional(),
});
export type BlueprintVisualFunction = z.infer<typeof BlueprintVisualFunctionSchema>;

export const BlueprintVisualPointSchema = z.object({
  id: z.string().min(1).max(64),
  name: z.string().max(80).optional(),
  x: BlueprintTemplatedNumberSchema,
  y: BlueprintTemplatedNumberSchema,
  color: z.string().max(40).optional(),
  label: z.string().max(160).optional(),
});
export type BlueprintVisualPoint = z.infer<typeof BlueprintVisualPointSchema>;

export const BlueprintFunctionGraphTemplateSchema = z.object({
  kind: z.literal('function_graph'),
  viewport: z
    .object({
      xMin: BlueprintTemplatedNumberSchema,
      xMax: BlueprintTemplatedNumberSchema,
      yMin: BlueprintTemplatedNumberSchema,
      yMax: BlueprintTemplatedNumberSchema,
    })
    .optional(),
  functions: z.array(BlueprintVisualFunctionSchema).min(1).max(4),
  points: z.array(BlueprintVisualPointSchema).max(12).optional().default([]),
});
export type BlueprintFunctionGraphTemplate = z.infer<typeof BlueprintFunctionGraphTemplateSchema>;

export const BlueprintTableTemplateSchema = z.object({
  kind: z.literal('table'),
  /** Cellules éventuellement templatées ({nom}, rendu via renderTemplate). */
  headers: z.array(z.string().max(400)).max(16).nullable().optional(),
  rows: z
    .array(z.array(z.string().max(800)).max(16))
    .min(1)
    .max(30),
});
export type BlueprintTableTemplate = z.infer<typeof BlueprintTableTemplateSchema>;

export const BlueprintVisualTemplateSchema = z.discriminatedUnion('kind', [
  BlueprintFunctionGraphTemplateSchema,
  BlueprintTableTemplateSchema,
]);
export type BlueprintVisualTemplate = z.infer<typeof BlueprintVisualTemplateSchema>;

// ── Spec complète ────────────────────────────────────────────────────────────

export const BlueprintSpecSchema = z.object({
  parameters: z.array(BlueprintParameterSchema).min(1).max(12),
  constraints: z.array(BlueprintConstraintSchema).max(20).default([]),
  templates: z.array(BlueprintTemplateSchema).min(1).max(4),
  solvePath: z.array(BlueprintSolveStepSchema).min(1).max(20),
  questions: z.array(BlueprintQuestionSchema).min(1).max(10),
  // PAS de .default(null) : un défaut matérialiserait `visual` comme requis
  // dans le type TS z.infer (comme `constraints`/`points` ci-dessus), ce qui
  // obligerait à l'ajouter dans TOUTE spec existante (aucune n'en a). Vraiment
  // optionnel — absent ⇒ undefined, traité comme null par le reste du code.
  visual: BlueprintVisualTemplateSchema.nullable().optional(),
});
export type BlueprintSpec = z.infer<typeof BlueprintSpecSchema>;

// ── Header (ligne `exercise_blueprints`) ─────────────────────────────────────

export const BlueprintStatusSchema = z.enum(['draft', 'active', 'archived']);
export type BlueprintStatus = z.infer<typeof BlueprintStatusSchema>;

export const BlueprintSourceKindSchema = z.enum(['teacher', 'student']);
export type BlueprintSourceKind = z.infer<typeof BlueprintSourceKindSchema>;

export const ExerciseBlueprintSchema = z.object({
  id: z.string().uuid(),
  title: z.string().min(1).max(160),
  level: SchoolLevelSchema,
  chapter_id: z.string().uuid().nullable(),
  source_statement: z.string().max(8000),
  source_corrige: z.string().max(8000),
  source_kind: BlueprintSourceKindSchema,
  spec: BlueprintSpecSchema,
  version: z.number().int().positive(),
  status: BlueprintStatusSchema,
  created_by: z.string().uuid(),
  created_at: z.string(),
  updated_at: z.string(),
});
export type ExerciseBlueprint = z.infer<typeof ExerciseBlueprintSchema>;

// ── Création (input edge function blueprint-build) ──────────────────────────

export const BuildBlueprintInputSchema = z.object({
  title: z.string().trim().min(1).max(160),
  level: SchoolLevelSchema,
  chapter_id: z.string().uuid().nullable().optional(),
  statement: z.string().trim().min(1).max(8000),
  corrige: z.string().trim().min(1).max(8000),
  source_kind: BlueprintSourceKindSchema,
});
export type BuildBlueprintInput = z.infer<typeof BuildBlueprintInputSchema>;
